import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import { Team } from '../../../interfaces/calls';
import teamsService from '../../../services/appointments/teams';
import { CustomTable } from '../../common/CustomTable';

type Props = {
  breadcrumbs: ReactNode;
};

export const TeamsCenter = ({ breadcrumbs }: Props) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamName, setTeamName] = useState('');
  
  const notify = useNotify();

  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      const response = await teamsService.getAllTeams();
      if (response.success && response.data) {
        setTeams(response.data);
      } else {
        notify('Failed to fetch teams', { type: 'error' });
      }
    } catch (error) {
      notify('Failed to fetch teams', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleOpenModal = (team?: Team) => {
    if (team) {
      setEditingTeam(team);
      setTeamName(team.name);
    } else {
      setEditingTeam(null);
      setTeamName('');
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTeam(null);
    setTeamName('');
  };

  const handleSave = async () => {
    try {
      if (editingTeam) {
        await teamsService.updateTeam(editingTeam.id, { name: teamName });
        notify('Team updated successfully', { type: 'success' });
      } else {
        await teamsService.createTeam({ name: teamName });
        notify('Team created successfully', { type: 'success' });
      }
      handleCloseModal();
      fetchTeams();
    } catch (error) {
      notify('Failed to save team', { type: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await teamsService.deleteTeam(id);
      notify('Team deleted successfully', { type: 'success' });
      fetchTeams();
    } catch (error) {
      notify('Failed to delete team', { type: 'error' });
    }
  };

  const rows = useMemo(
    () =>
      teams.map((team) => ({
        name: <Typography variant='body2'>{team.name}</Typography>,
        memberCount: <Typography variant='body2'>{team._count?.AgentTeam || 0}</Typography>,
        actions: (
            <Box>
                <IconButton size='small' onClick={() => handleOpenModal(team)}>
                    <EditIcon fontSize='small' />
                </IconButton>
                <IconButton size='small' onClick={() => handleDelete(team.id)}>
                    <DeleteIcon fontSize='small' />
                </IconButton>
            </Box>
        ),
      })),
    [teams],
  );

  return (
    <Box className='tab-section' marginTop={0}>
      <Box className='agents-center-header'>
        <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom='16px'>
          {breadcrumbs}
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Create Team
          </Button>
        </Box>
      </Box>

      <CustomTable
        columns={[
          { key: 'name', label: 'Team Name' },
          { key: 'memberCount', label: 'Members' },
          { key: 'actions', label: 'Actions', width: '100px' },
        ]}
        defaultText='No teams found'
        key='teams-center-table'
        loading={loading}
        rows={rows}
        pagination={{
            rowsPerPageOptions: [100],
            rowPerPage: 100,
            page: 1,
            totalCount: teams.length,
            onPageChange: () => {},
        }}
      />

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth='sm' fullWidth>
        <DialogTitle>{editingTeam ? 'Edit Team' : 'Create Team'}</DialogTitle>
        <DialogContent>
            <Box mt={2}>
                <TextField
                    fullWidth
                    label="Team Name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                />
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={!teamName.trim()}>
                Save
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
